import { ChangeEvent, ComponentProps, useEffect, useMemo, useRef } from 'react';
import {
  type DayButton,
  DayPicker,
  type DropdownProps,
  getDefaultClassNames,
} from 'react-day-picker';
import { enUS, uk } from 'react-day-picker/locale';
import { useTranslation } from 'react-i18next';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react';

import { Button, buttonVariants } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { createCalendarFormatters } from '@/lib/calendar-locale';
import { cn } from '@/lib/utils';

const Calendar = (
  props: ComponentProps<typeof DayPicker> & {
    buttonVariant?: ComponentProps<typeof Button>['variant'];
  },
) => {
  const {
    className,
    classNames,
    showOutsideDays = true,
    captionLayout = 'label',
    buttonVariant = 'ghost',
    formatters,
    labels,
    components,
    ...restProps
  } = props;
  const defaultClassNames = getDefaultClassNames();
  const { i18n } = useTranslation();

  const locale = useMemo(() => {
    return i18n.language === 'uk' ? uk : enUS;
  }, [i18n.language]);

  const calendarFormatters = useMemo(
    () => ({
      ...createCalendarFormatters(locale),
      ...formatters,
    }),
    [locale, formatters],
  );

  const calendarLabels = useMemo(
    () => ({
      ...labels,
    }),
    [labels],
  );

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      weekStartsOn={1}
      locale={locale}
      className={cn(
        'bg-background group/calendar p-3 [--cell-size:--spacing(8)] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      formatters={calendarFormatters}
      labels={calendarLabels}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn(
          'flex gap-4 flex-col md:flex-row relative',
          defaultClassNames.months,
        ),
        month: cn('flex flex-col w-full gap-4', defaultClassNames.month),
        nav: cn(
          'flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between',
          defaultClassNames.nav,
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          'size-10 [&_svg]:size-5 text-muted-foreground aria-disabled:opacity-50 p-0 has-[>svg]:px-0 select-none',
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          'size-10 [&_svg]:size-5 text-muted-foreground aria-disabled:opacity-50 p-0 has-[>svg]:px-0 select-none',
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          'flex items-center justify-center h-(--cell-size) w-full px-(--cell-size)',
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          'w-full flex items-center text-sm font-medium justify-center h-(--cell-size) gap-2',
          defaultClassNames.dropdowns,
        ),
        caption_label: cn(
          'select-none text-sm font-medium capitalize',
          defaultClassNames.caption_label,
        ),
        month_grid: 'w-full border-collapse',
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn(
          'text-muted-foreground rounded-md flex-1 text-xs font-medium h-6 select-none',
          defaultClassNames.weekday,
        ),
        week: cn('flex w-full mt-1', defaultClassNames.week),
        week_number_header: cn(
          'select-none w-(--cell-size)',
          defaultClassNames.week_number_header,
        ),
        week_number: cn(
          'text-xs select-none text-muted-foreground',
          defaultClassNames.week_number,
        ),
        day: cn(
          'relative p-0 text-center [&:last-child[data-selected=true]_button]:rounded-r-md group/day select-none w-full',
          restProps.showWeekNumber
            ? '[&:nth-child(2)[data-selected=true]_button]:rounded-l-md'
            : '[&:first-child[data-selected=true]_button]:rounded-l-md',
          defaultClassNames.day,
        ),
        range_start: cn(
          'rounded-l-md bg-accent',
          defaultClassNames.range_start,
        ),
        range_middle: cn('rounded-none', defaultClassNames.range_middle),
        range_end: cn('rounded-r-md bg-accent', defaultClassNames.range_end),
        today: cn(
          'text-accent-foreground [&>button]:bg-accent',
          defaultClassNames.today,
        ),
        outside: cn(
          'text-muted-foreground aria-selected:text-muted-foreground',
          defaultClassNames.outside,
        ),
        disabled: cn(
          'text-muted-foreground opacity-50',
          defaultClassNames.disabled,
        ),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: (rootProps) => {
          const { className, rootRef, ...restRootProps } = rootProps;

          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...restRootProps}
            />
          );
        },
        Chevron: (chevrProps) => {
          const { className, orientation, ...restChevrProps } = chevrProps;

          if (orientation === 'left') {
            return (
              <ChevronLeftIcon
                className={cn('size-4', className)}
                {...restChevrProps}
              />
            );
          }

          if (orientation === 'right') {
            return (
              <ChevronRightIcon
                className={cn('size-4', className)}
                {...restChevrProps}
              />
            );
          }

          return (
            <ChevronDownIcon
              className={cn('size-4', className)}
              {...restChevrProps}
            />
          );
        },
        DayButton: CalendarDayButton,
        Dropdown: CalendarDropdown,
        WeekNumber: (weekProps) => {
          const { children, ...restWeekProps } = weekProps;

          return (
            <td {...restWeekProps}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>
          );
        },
        ...components,
      }}
      {...restProps}
    />
  );
};

const CalendarDropdown = (props: DropdownProps) => {
  const { options, value, onChange, disabled, 'aria-label': ariaLabel } = props;
  const selectedOption = options?.find((option) => option.value === value);

  return (
    <Select
      value={value === undefined ? undefined : String(value)}
      disabled={disabled}
      onValueChange={(nextValue) => {
        onChange?.({
          target: { value: nextValue },
        } as ChangeEvent<HTMLSelectElement>);
      }}
    >
      <SelectTrigger
        aria-label={ariaLabel}
        className="bg-card hover:bg-accent border-input data-[state=open]:border-primary [&_svg]:text-muted-foreground relative gap-2 rounded-lg pr-2 pl-3 font-medium capitalize shadow-none focus-visible:ring-0 data-[size=default]:h-10 [&_svg]:size-5 [&_svg]:opacity-100"
      >
        <SelectValue>{selectedOption?.label}</SelectValue>
      </SelectTrigger>
      <SelectContent
        align="start"
        className="z-110 max-h-72"
      >
        {options?.map((option) => (
          <SelectItem
            key={option.value}
            value={String(option.value)}
            disabled={option.disabled}
            className="h-10 capitalize"
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const CalendarDayButton = (props: ComponentProps<typeof DayButton>) => {
  const { className, day, modifiers, children, ...restProps } = props;
  const defaultClassNames = getDefaultClassNames();

  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        'text-sm data-[selected-single=true]:border-primary data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground data-[range-middle=true]:bg-primary-soft data-[range-middle=true]:text-accent-foreground data-[range-start=true]:border-primary data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[range-end=true]:border-primary data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-ring/50 dark:hover:text-accent-foreground flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 border-2 border-transparent font-medium group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] data-[range-end=true]:rounded-md data-[range-end=true]:rounded-r-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md data-[range-start=true]:rounded-l-md data-[selected-single=true]:font-semibold [&>span]:text-xs [&>span]:opacity-70',
        'mx-auto size-(--cell-size) w-(--cell-size) min-w-0 rounded-lg group-data-[focused=true]/day:ring-0',
        defaultClassNames.day,
        className,
      )}
      {...restProps}
    >
      {children}
    </Button>
  );
};

export { Calendar, CalendarDayButton };
